import { useEffect, useState } from 'react';

import { CRUMBS, EVENTS, TABS } from '../fitment-constants';

export default function useSize({
  getTireCrossDimensions,
  getTireDiameters,
  getTireDimensions,
  getWheelBoltPatterns,
  getWheelDiameters,
  getWheelWidths,
  queryErrorEvent,
  selectedSizeTab,
  selectedTab
}) {
  const [selectedTireCrossDimension, setSelectedTireCrossDimension] =
    useState('');
  const [selectedTireDiameter, setSelectedTireDiameter] = useState('');
  const [selectedTireHeight, setSelectedTireHeight] = useState('');
  const [selectedTireSizeCrumb, setSelectedTireSizeCrumb] = useState(
    CRUMBS.WIDTH
  );
  const [selectedTireWidth, setSelectedTireWidth] = useState('');
  const [selectedWheelBoltPattern, setSelectedWheelBoltPattern] = useState('');
  const [selectedWheelDiameter, setSelectedWheelDiameter] = useState('');
  const [selectedWheelSizeCrumb, setSelectedWheelSizeCrumb] = useState(
    CRUMBS.DIAMETER
  );
  const [selectedWheelWidth, setSelectedWheelWidth] = useState('');
  const [tireCrossDimensions, setTireCrossDimensions] = useState([]);
  const [tireDiameters, setTireDiameters] = useState([]);
  const [tireDimensions, setTireDimensions] = useState(null);
  const [wheelBoltPatterns, setWheelBoltPatterns] = useState([]);
  const [wheelDiameters, setWheelDiameters] = useState([]);
  const [wheelWidths, setWheelWidths] = useState([]);

  const [frontTireSize, setFrontTireSize] = useState(null);
  const [isEditingFrontTireSize, setIsEditingFrontTireSize] = useState(false);
  const [isEditingRearTireSize, setIsEditingRearTireSize] = useState(false);
  const [isTireCodeDescriptionOpen, setIsTireCodeDescriptionOpen] =
    useState(false);
  const [rearTireSize, setRearTireSize] = useState(null);
  const [showRemoveRearTireConfirmation, setShowRemoveRearTireConfirmation] =
    useState(false);
  const [sizeLimit, setSizeLimit] = useState(18);

  const [frontWheelSize, setFrontWheelSize] = useState(null);
  const [rearWheelSize, setRearWheelSize] = useState(null);
  const [isEditingFrontWheelSize, setIsEditingFrontWheelSize] = useState(false);
  const [isEditingRearWheelSize, setIsEditingRearWheelSize] = useState(false);
  const [showRemoveRearWheelConfirmation, setShowRemoveRearWheelConfirmation] =
    useState(false);

  useEffect(() => {
    if (selectedTab !== TABS.SIZE) return;
    if (
      selectedSizeTab === TABS.TIRES &&
      ((selectedTireSizeCrumb === CRUMBS.WIDTH && !selectedTireHeight) ||
        selectedTireSizeCrumb === CRUMBS.HEIGHT)
    ) {
      getTireDimensions()
        .then(setTireDimensions)
        .catch(queryErrorEvent(EVENTS.ERROR_QUERY_GET_TIRE_DIMENSIONS));
    } else {
      getWheelDiameters()
        .then(setWheelDiameters)
        .catch(queryErrorEvent(EVENTS.ERROR_QUERY_GET_WHEEL_DIAMETERS));
    }
  }, [
    selectedTab,
    selectedSizeTab,
    selectedTireSizeCrumb,
    selectedTireHeight,
    getTireDimensions,
    queryErrorEvent,
    getWheelDiameters
  ]);

  useEffect(() => {
    if (
      !(
        selectedWheelDiameter &&
        Array.isArray(wheelWidths) &&
        wheelWidths.length === 0
      )
    )
      return;
    getWheelWidths(selectedWheelDiameter)
      .then(setWheelWidths)
      .catch(queryErrorEvent(EVENTS.ERROR_QUERY_GET_WHEEL_WIDTHS));
  }, [getWheelWidths, queryErrorEvent, selectedWheelDiameter, wheelWidths]);

  useEffect(() => {
    if (
      !(
        selectedWheelDiameter &&
        selectedWheelWidth &&
        Array.isArray(wheelBoltPatterns) &&
        wheelBoltPatterns.length === 0
      )
    )
      return;
    getWheelBoltPatterns(selectedWheelDiameter, selectedWheelWidth)
      .then(setWheelBoltPatterns)
      .catch(queryErrorEvent(EVENTS.ERROR_QUERY_GET_WHEEL_BOLT_PATTERNS));
  }, [
    getWheelBoltPatterns,
    queryErrorEvent,
    selectedWheelDiameter,
    selectedWheelWidth,
    wheelBoltPatterns
  ]);

  useEffect(() => {
    if (selectedTab !== TABS.SIZE) return;
    if (
      selectedSizeTab === TABS.TIRES &&
      ((selectedTireSizeCrumb === CRUMBS.WIDTH && selectedTireHeight) ||
        selectedTireSizeCrumb === CRUMBS.RATIO)
    ) {
      getTireCrossDimensions(selectedTireHeight || selectedTireWidth)
        .then(setTireCrossDimensions)
        .catch(queryErrorEvent(EVENTS.ERROR_QUERY_GET_TIRE_CROSS_DIMENSIONS));
    }
  }, [
    selectedTab,
    selectedSizeTab,
    selectedTireSizeCrumb,
    selectedTireHeight,
    getTireCrossDimensions,
    selectedTireWidth,
    queryErrorEvent
  ]);

  useEffect(() => {
    if (selectedTab !== TABS.SIZE) return;
    if (
      selectedSizeTab === TABS.TIRES &&
      selectedTireSizeCrumb === CRUMBS.DIAMETER
    ) {
      getTireDiameters(
        selectedTireHeight || selectedTireWidth,
        selectedTireCrossDimension
      )
        .then(setTireDiameters)
        .catch(queryErrorEvent(EVENTS.ERROR_QUERY_GET_TIRE_DIAMETERS));
    }
  }, [
    selectedTab,
    selectedSizeTab,
    selectedTireCrossDimension,
    selectedTireSizeCrumb,
    getTireDiameters,
    selectedTireHeight,
    selectedTireWidth,
    queryErrorEvent
  ]);

  function resetTireCrossDimensions() {
    setSelectedTireCrossDimension('');
    setTireCrossDimensions([]);
  }

  function resetTireDiameters() {
    setSelectedTireDiameter('');
    setTireDiameters([]);
  }

  function resetWheelBoltPatterns() {
    setSelectedWheelBoltPattern('');
    setWheelBoltPatterns([]);
  }

  function resetWheelWidths() {
    setWheelWidths([]);
  }

  return {
    frontTireSize,
    frontWheelSize,
    isEditingFrontTireSize,
    isEditingFrontWheelSize,
    isEditingRearTireSize,
    isEditingRearWheelSize,
    isTireCodeDescriptionOpen,
    rearTireSize,
    rearWheelSize,
    resetTireCrossDimensions,
    resetTireDiameters,
    resetWheelBoltPatterns,
    resetWheelWidths,
    selectedTireCrossDimension,
    selectedTireDiameter,
    selectedTireHeight,
    selectedTireSizeCrumb,
    selectedTireWidth,
    selectedWheelBoltPattern,
    selectedWheelDiameter,
    selectedWheelSizeCrumb,
    selectedWheelWidth,
    setFrontTireSize,
    setFrontWheelSize,
    setIsEditingFrontTireSize,
    setIsEditingFrontWheelSize,
    setIsEditingRearTireSize,
    setIsEditingRearWheelSize,
    setIsTireCodeDescriptionOpen,
    setRearTireSize,
    setRearWheelSize,
    setSelectedTireCrossDimension,
    setSelectedTireDiameter,
    setSelectedTireHeight,
    setSelectedTireSizeCrumb,
    setSelectedTireWidth,
    setSelectedWheelBoltPattern,
    setSelectedWheelDiameter,
    setSelectedWheelSizeCrumb,
    setSelectedWheelWidth,
    setShowRemoveRearTireConfirmation,
    setShowRemoveRearWheelConfirmation,
    setSizeLimit,
    showRemoveRearTireConfirmation,
    showRemoveRearWheelConfirmation,
    sizeLimit,
    tireCrossDimensions,
    tireDiameters,
    tireDimensions,
    wheelBoltPatterns,
    wheelDiameters,
    wheelWidths
  };
}
